import React, { useState } from 'react';
import { getAuth, EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { X, Lock, Save, Loader2, CheckCircle, Eye, EyeOff } from 'lucide-react';
import './ChangePasswordPanel.css';

interface Props {
  onClose: () => void;
}

const ChangePasswordPanel: React.FC<Props> = ({ onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const MIN_PASSWORD_LEN = 12;
  const isValid =
    currentPassword.length >= 1
    && newPassword.length >= MIN_PASSWORD_LEN
    && newPassword === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    setSaving(true);
    setStatus('idle');
    setErrorMsg('');

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user || !user.email) throw new Error('Not signed in');

      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      setStatus('success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => onClose(), 1500);
    } catch (err: any) {
      setStatus('error');
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setErrorMsg('Current password is incorrect.');
      } else if (err.code === 'auth/weak-password') {
        setErrorMsg('New password is too weak. Use at least 12 characters.');
      } else {
        setErrorMsg(err.message || 'Failed to update password.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="chpw-overlay" onClick={onClose}>
      <div className="chpw-panel" onClick={e => e.stopPropagation()}>
        <div className="chpw-header">
          <div className="chpw-header-title">
            <Lock size={18} />
            <h2>Change Password</h2>
          </div>
          <button className="chpw-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <form className="chpw-body" onSubmit={handleSubmit}>
          <div className="chpw-field">
            <label>Current Password</label>
            <div className="chpw-input-wrap">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={e => { setCurrentPassword(e.target.value); setStatus('idle'); }}
                placeholder="Enter current password"
                autoComplete="current-password"
              />
              <button type="button" className="chpw-eye" onClick={() => setShowCurrent(v => !v)}>
                {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <div className="chpw-field">
            <label>New Password</label>
            <div className="chpw-input-wrap">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); setStatus('idle'); }}
                placeholder="At least 12 characters"
                autoComplete="new-password"
              />
              <button type="button" className="chpw-eye" onClick={() => setShowNew(v => !v)}>
                {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <div className="chpw-field">
            <label>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); setStatus('idle'); }}
              placeholder="Re-enter new password"
              autoComplete="new-password"
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <span className="chpw-mismatch">Passwords do not match</span>
            )}
          </div>

          {status === 'error' && (
            <div className="chpw-error">{errorMsg}</div>
          )}

          <button className="chpw-save" type="submit" disabled={saving || !isValid}>
            {saving ? (
              <><Loader2 size={14} className="spin" /> Updating...</>
            ) : status === 'success' ? (
              <><CheckCircle size={14} /> Password Updated</>
            ) : (
              <><Save size={14} /> Update Password</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordPanel;
